import { Form, useActionData } from "@remix-run/react";
import type { ActionFunction, LoaderFunction } from "@remix-run/server-runtime";
import { redirect } from "@remix-run/server-runtime";
import Button from "~/ui/Button";
import DefaultLayout from "~/ui/DefaultLayout";
import Heading from "~/ui/Heading";
import Input from "~/ui/Input";
import isValidEmail from "~/kernel/email/helpers/isValidEmail";
import type { MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import ROUTES from "~/kernel/routing/ROUTES";
import createPasswordReset from "~/routes/password-resets.new/createPasswordReset";
import Alert from "~/ui/Alert";
import Link from "~/ui/Link";
import Card from "~/ui/Card";
import title from "~/kernel/meta/title";
import useIsWorking from "~/kernel/server/useIsWorking";
import userRepository from "~/repositories/userRepository";
import parseFormData from "~/kernel/form/parseFormData";
import { isString } from "@tiptap/react";
import normalizeEmail from "~/kernel/strings/normalizeEmail";
import useSearchParam from "~/kernel/searchParams/useSearchParam";
import recordServerAnalyticsEvent from "~/analytics/services/recordServerAnalyticsEvent.server";

interface ActionData {
  errors?: {
    email?: string;
    server?: string;
  };
}

export const meta: MetaFunction = () => [
  { title: title("Återställ lösenord") },
  { description: "Återställ lösenord." },
];

export const loader: LoaderFunction = async ({ request }) => {
  await recordServerAnalyticsEvent(
    {
      type: "PASSWORD_RESET_REQUEST_VIEWED_V1",
      payload: {},
      recordedAt: new Date(),
    },
    { request, userId: null },
  );

  return json({});
};

export const action: ActionFunction = async ({ request }) => {
  const formDataResult = await parseFormData(request);

  if (!formDataResult.isSuccessful) {
    return json<ActionData>({
      errors: { server: "Ogiltigt anrop. Vänligen kontakta support." },
    });
  }

  const emailValue = formDataResult.formData.get("email");

  if (!isString(emailValue)) {
    return json<ActionData>(
      { errors: { email: "E-post är inte giltig." } },
      { status: 400 },
    );
  }

  const email = normalizeEmail(emailValue);

  if (!isValidEmail(email)) {
    return json<ActionData>(
      { errors: { email: "E-post är inte giltig." } },
      { status: 400 },
    );
  }

  const user = await userRepository().findByEmail(email);

  await recordServerAnalyticsEvent(
    {
      type: "PASSWORD_RESET_REQUESTED_V1",
      payload: { hasAccount: Boolean(user) },
      recordedAt: new Date(),
    },
    { request, userId: user?.id ?? null },
  );

  /**
   * If user does not exist, we should not give away any hints to hackers. Thus
   * we will display the same page as if the user did exist.
   */
  if (!user) {
    return redirect(
      ROUTES.passwordReset.confirmation.path({
        params: { email },
      }),
    );
  }

  return createPasswordReset({ user }).catch(async (error) => {
    reportError({
      tag: "passwor-resets/new",
      error,
      additionalInfo: {
        userId: user.id,
      },
    });
    return json<ActionData>(
      {
        errors: {
          server:
            "Något gick fel när vi skulle återställa ditt lösenord. " +
            "Var vänlig försök igen senare.",
        },
      },
      { status: 500 },
    );
  });
};

export default function NewPasswordReset() {
  const actionData = useActionData() as ActionData;
  const isWorking = useIsWorking();
  const [defaultEmail] = useSearchParam("email");

  return (
    <DefaultLayout>
      <div className="mx-auto md:max-w-lg">
        <Card>
          <div className="space-y-8">
            <Heading>Återställ lösenord</Heading>
            <p>
              Ange den e-post du använt då du skapat ditt konto så skickar vi
              dig en länk för att återställa ditt lösenord.
            </p>
            <Form method="post" className="space-y-8">
              <Input
                id="email"
                defaultValue={defaultEmail}
                label="E-post"
                required
                autocomplete="email"
                error={actionData?.errors?.email}
              />
              <Button loading={isWorking} type="submit">
                Återställ lösenord
              </Button>
            </Form>
            {actionData?.errors?.server && (
              <Alert type="error">{actionData?.errors?.server}</Alert>
            )}
            <p className="text-center">
              <Link to={ROUTES.login.path()}>Tillbaka till inloggning.</Link>
            </p>
          </div>
        </Card>
      </div>
    </DefaultLayout>
  );
}
